import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

export const PLATFORM_ROLES = [
    ['platform_owner', 'Platform Owner'],
    ['super_admin', 'Super Admin'],
    ['platform_admin', 'Platform Admin'],
];

export default function PlatformRolesView() {
    const [directory, setDirectory] = useState(null);
    const [target, setTarget] = useState('');
    const [role, setRole] = useState('platform_admin');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        const { data, error: failure } = await supabaseClient.rpc('bl407_platform_directory');
        if (failure) throw failure;
        setDirectory(data);
    }, []);
    useEffect(() => {
        let active = true;
        load().catch((failure) => { if (active) setError(failure.message); });
        return () => { active = false; };
    }, [load]);

    async function save(userId, nextRole) {
        setBusy(true); setError('');
        try {
            const { error: failure } = await supabaseClient.rpc('bl407_set_platform_role', {
                p_user_id: userId, p_role: nextRole || null,
            });
            if (failure) throw failure;
            await load();
            setTarget('');
        } catch (failure) {
            setError(failure.message || 'The platform role could not be changed.');
        } finally { setBusy(false); }
    }
    return (
        <section className="ia-admin p-6 text-[var(--ink)]">
            <h1 className="mb-3 text-2xl font-semibold">Platform roles</h1>
            <p className="mb-4">Only Platform Owner and Super Admin can change this list. Platform Admin can use every sign-in door, but does not receive the master key to company data.</p>
            {error && <p role="alert" className="mb-4 text-red-600">{error}</p>}
            {!directory && !error && <p role="status">Loading platform roles...</p>}
            {directory && (
                <>
                    <ul className="space-y-4">
                        {directory.roles.map((person) => (
                            <li key={person.user_id} className="flex flex-wrap items-center gap-3">
                                <span>{person.email}</span>
                                <select aria-label={`Role for ${person.email}`} value={person.role} disabled={busy}
                                    onChange={(event) => save(person.user_id, event.target.value)}>
                                    {PLATFORM_ROLES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                </select>
                                <button disabled={busy} onClick={() => save(person.user_id, null)}>Remove platform role</button>
                            </li>
                        ))}
                    </ul>
                    <form onSubmit={(event) => { event.preventDefault(); save(target.trim(), role); }} className="my-6 flex flex-wrap gap-3">
                        <label>Confirmed invited login ID
                            <input required value={target} onChange={(event) => setTarget(event.target.value)}
                                className="ml-2 rounded border p-2" placeholder="Auth user UUID" />
                        </label>
                        <select aria-label="New platform role" value={role} onChange={(event) => setRole(event.target.value)}>
                            {PLATFORM_ROLES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                        <button disabled={busy} type="submit">Assign role</button>
                    </form>
                    <h2 className="mb-2 font-semibold">Waiting for a normal invitation and confirmed login</h2>
                    {directory.pending.map((person) => <p key={person.email}>{person.email} - {PLATFORM_ROLES.find(([key]) => key === person.role)?.[1]}</p>)}
                    <h2 className="mb-2 mt-6 font-semibold">Recent role changes</h2>
                    <ul>{directory.audit.map((item) => (
                        <li key={item.id} className="mb-2 text-sm">
                            {item.changed_at}: {item.user_id}, {item.old_role || 'no role'} to {item.new_role || 'no role'};
                            changed by {item.actor_id || 'approved migration / invitation claim'}.
                        </li>
                    ))}</ul>
                </>
            )}
        </section>
    );
}
